import {
  ArticleOutlined,
  BusinessOutlined,
  CodeOutlined,
  EngineeringOutlined,
  HubOutlined,
  PrecisionManufacturingOutlined,
  QueryStatsOutlined,
  ScienceOutlined,
  StarBorderOutlined,
  VerifiedOutlined,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  Container,
  Grid,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';

const proofPoints = [
  {
    value: 'Section 801',
    label: 'first DoD commercial software license path for FEMORPH',
  },
  {
    value: '5,400',
    label: 'GitHub stars across public repos listed below, as of May 22, 2026',
  },
  {
    value: 'PyVista + PyAnsys',
    label: 'scientific Python and CAE automation ecosystems AEA helped create and scale',
  },
  {
    value: '41',
    label: 'public research and conference entries supporting this technical lineage',
  },
];

const companyCapabilities = [
  {
    icon: <PrecisionManufacturingOutlined />,
    title: 'As-manufactured model creation',
    text: 'AEA builds workflows that turn optical scans, CMM data, CT reconstructions, and CAD references into analysis-ready finite element models.',
  },
  {
    icon: <HubOutlined />,
    title: 'Mesh metamorphosis',
    text: 'FEMORPH updates existing analysis meshes to measured geometry while preserving topology, metadata, boundary intent, and downstream solver workflows.',
  },
  {
    icon: <QueryStatsOutlined />,
    title: 'Modal and test correlation',
    text: 'AEA work spans SLV, NSMS, strain gauge, TWE, and modal correlation pipelines used to compare measured structures against nominal and as-measured FEMs.',
  },
  {
    icon: <EngineeringOutlined />,
    title: 'Repair and sustainment',
    text: 'The company has supported blend repair, edge repair, cyclic-sector workflows, model debugging, quality checks, and production support for turbine hardware.',
  },
  {
    icon: <ScienceOutlined />,
    title: 'Research-grade validation',
    text: 'FEMORPH is backed by published research in geometric mistuning, model updating, strain-energy convergence, testing limits, and as-manufactured rotor analysis.',
  },
  {
    icon: <CodeOutlined />,
    title: 'Production software engineering',
    text: 'AEA maintains private Python, C++, GUI, solver, licensing, CI, documentation, and deployment systems around the FEMORPH product family.',
  },
];

const companyLineage = [
  'AEA created and maintains FEMORPH, the finite element model updating technology behind the commercial product.',
  'AEA helped bring the PyAnsys concept from an independent Python bridge into an official open-source ecosystem for engineering simulation.',
  'AEA has helped create and sustain PyVista, a widely used open-source Python library for 3D visualization and mesh analysis on top of VTK.',
  'AEA engineering has contributed to PyMAPDL, PyFluent, PyAEDT, pymeshfix, tetgen, pyacvd, and related solver, mesh, and automation tools.',
  'AEA work is grounded in a long-running research record in finite element analysis, mesh metamorphosis, geometric mistuning, turbine engine fatigue, and dynamic testing.',
  'AEA technical work is connected to patents covering turbine airfoil repair and improving strain-gauge to blade-tip-timing correlation.',
];

const productStack = [
  {
    name: 'femorph',
    detail: 'core C++ and Python mesh metamorphosis engine for FE-to-scan updates',
  },
  {
    name: 'femorph-gui',
    detail: 'desktop application and installer for interactive engineering workflows',
  },
  {
    name: 'femorph-blender',
    detail: 'scalloped-blend and adjacent-surface repair workflow support for bladed FEMs',
  },
  {
    name: 'femorph-cfd',
    detail: 'CFD mesh metamorphosis adapter for scan-informed flow-domain updates',
  },
  {
    name: 'readers and solvers',
    detail: 'MAPDL archive, result, mesh, modal, solver, and verification infrastructure',
  },
  {
    name: 'automation systems',
    detail: 'private PyPI, license services, CI runners, documentation, and release pipelines',
  },
];

const openSourceProjects = [
  {
    name: 'PyVista',
    repo: 'pyvista/pyvista',
    stars: '3,670',
    href: 'https://github.com/pyvista/pyvista',
    detail: '3D visualization and mesh analysis for science and engineering.',
  },
  {
    name: 'PyMAPDL',
    repo: 'ansys/pymapdl',
    stars: '492',
    href: 'https://github.com/ansys/pymapdl',
    detail: 'Core PyAnsys client library for programmable MAPDL workflows.',
  },
  {
    name: 'PyAnsys',
    repo: 'ansys/pyansys',
    stars: '261',
    href: 'https://github.com/ansys/pyansys',
    detail: 'Open-source Python ecosystem connecting Ansys simulation products with modern automation workflows.',
  },
  {
    name: 'pymeshfix',
    repo: 'pyvista/pymeshfix',
    stars: '386',
    href: 'https://github.com/pyvista/pymeshfix',
    detail: 'Python wrapper for MeshFix surface mesh repair workflows.',
  },
  {
    name: 'tetgen',
    repo: 'pyvista/tetgen',
    stars: '301',
    href: 'https://github.com/pyvista/tetgen',
    detail: 'Python interface for tetrahedral mesh generation from 3D domains.',
  },
  {
    name: 'pyacvd',
    repo: 'pyvista/pyacvd',
    stars: '286',
    href: 'https://github.com/pyvista/pyacvd',
    detail: 'Surface mesh resampling and clustering for uniform mesh workflows.',
  },
];

const researchThemes = [
  'Automated finite element mesh updating from measured geometry',
  'Geometric mistuning models for integrally bladed rotors',
  'Experimental validation using system identification and traveling wave excitation',
  'Blade tip timing and strain gauge limit placement using as-manufactured geometry',
  'As-manufactured CFD, modal, and repair response studies',
  'Open-source 3D visualization and mesh analysis through PyVista',
];

const publicLinks = [
  {
    label: 'Founder public resume',
    href: 'https://akaszynski.github.io/resume/',
  },
  {
    label: 'PyVista',
    href: 'https://github.com/pyvista/pyvista',
  },
  {
    label: 'PyMAPDL',
    href: 'https://github.com/ansys/pymapdl',
  },
  {
    label: 'AFRL FEMORPH license announcement',
    href: 'https://www.wpafb.af.mil/News/Article-Display/Article/1503043/afrl-signs-first-of-its-kind-software-license-with-pratt-whitney/',
  },
  {
    label: 'Federal Laboratory Consortium award',
    href: 'https://federallabs.org/successes/awards/awards-gallery/2019/femorph-section-801-software-license-first-in-department-of',
  },
];

const About: React.FC = () => {
  const theme = useTheme();

  return (
    <Box sx={{ bgcolor: 'background.default' }}>
      <Box component="section" sx={{ bgcolor: '#172126', color: 'white' }}>
        <Container maxWidth="xl" sx={{ py: { xs: 7, md: 11 } }}>
          <Grid container spacing={{ xs: 5, md: 8 }} alignItems="center">
            <Grid item xs={12} md={7}>
              <Chip
                label="Company behind FEMORPH"
                sx={{
                  mb: 3,
                  color: 'white',
                  bgcolor: alpha(theme.palette.secondary.main, 0.8),
                  fontWeight: 700,
                }}
              />
              <Typography variant="h2" sx={{ maxWidth: 900, mb: 2, fontSize: { xs: '2.5rem', md: '4.5rem' }, lineHeight: 1 }}>
                Advanced Engineering Analysis LLC
              </Typography>
              <Typography variant="h5" component="p" sx={{ maxWidth: 820, color: 'rgba(255,255,255,0.82)', fontWeight: 400, lineHeight: 1.55, mb: 4 }}>
                AEA is a specialized engineering software company focused on high-consequence finite element analysis, mesh metamorphosis, scan-to-model automation, and production FEMORPH support for aerospace and advanced manufacturing teams.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Button component={RouterLink} to="/features" variant="contained" size="large" endIcon={<VerifiedOutlined />}>
                  FEMORPH capabilities
                </Button>
                <Button
                  component={RouterLink}
                  to="/case-studies"
                  variant="outlined"
                  size="large"
                  sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.58)', '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.08)' } }}
                >
                  Case studies
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12} md={5}>
              <Box
                sx={{
                  bgcolor: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(255,255,255,0.16)',
                  p: { xs: 2.5, md: 3 },
                }}
              >
                <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                  Built for demanding engineering workflows
                </Typography>
                <Typography variant="body1" sx={{ mt: 1, color: 'rgba(255,255,255,0.82)', lineHeight: 1.75 }}>
                  FEMORPH is not a generic visualization tool. It is the product of years of finite element research, fielded aerospace analysis, production debugging, and software engineering around real measured hardware.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box component="section" sx={{ bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Container maxWidth="xl" sx={{ py: { xs: 4, md: 5 } }}>
          <Grid container spacing={2.5}>
            {proofPoints.map((point) => (
              <Grid item xs={12} sm={6} md={3} key={point.value}>
                <Box sx={{ height: '100%', borderLeft: '3px solid', borderColor: 'secondary.main', pl: 2.5, py: 0.5 }}>
                  <Typography variant="h4">{point.value}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                    {point.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box component="section" sx={{ bgcolor: 'background.default' }}>
        <Container maxWidth="xl" sx={{ py: { xs: 7, md: 10 } }}>
          <Grid container spacing={{ xs: 4, md: 7 }} alignItems="flex-start">
            <Grid item xs={12} md={5}>
              <Typography variant="overline" color="secondary.dark">
                Company overview
              </Typography>
              <Typography variant="h3" sx={{ mt: 1, mb: 2 }}>
                Engineering software from people who do the engineering.
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.05rem', lineHeight: 1.8 }}>
                Advanced Engineering Analysis LLC develops and maintains FEMORPH as professional engineering software for teams that need accurate as-manufactured models from as-measured geometry. AEA work spans scan registration, finite element model updating, modal correlation, repair modeling, CFD mesh adaptation, automated testing, solver verification, licensing, and release infrastructure.
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.05rem', lineHeight: 1.8, mt: 2 }}>
                The company supports workflows for government research laboratories, aerospace OEMs, manufacturing teams, and sustainment organizations. The common thread is practical: preserve trusted engineering models while updating them to match real, measured hardware.
              </Typography>
            </Grid>
            <Grid item xs={12} md={7}>
              <Grid container spacing={2.5}>
                {companyCapabilities.map((item) => (
                  <Grid item xs={12} sm={6} key={item.title}>
                    <Box sx={{ height: '100%', p: 2.5, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
                      <Box sx={{ color: 'primary.main', mb: 1.5, '& svg': { fontSize: 30 } }}>
                        {item.icon}
                      </Box>
                      <Typography variant="h6" sx={{ mb: 1 }}>
                        {item.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                        {item.text}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box component="section" sx={{ bgcolor: '#eef3f1', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Container maxWidth="xl" sx={{ py: { xs: 7, md: 10 } }}>
          <Grid container spacing={{ xs: 5, md: 8 }}>
            <Grid item xs={12} md={4}>
              <Typography variant="overline" color="secondary.dark">
                Open-source credibility
              </Typography>
              <Typography variant="h3" sx={{ mt: 1, mb: 2 }}>
                Public engineering software with real adoption.
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                AEA's engineering approach is grounded in the same software culture behind PyVista and PyAnsys: tested Python interfaces, reusable mesh tooling, documentation, and maintainable APIs that engineers can build on.
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8, mt: 2 }}>
                PyAnsys began as an independent open-source bridge between Ansys simulation software and Python. It grew into an official company-scale ecosystem by pairing decentralized developer ownership with shared documentation, GitHub Actions, automated formatting, testing, and release guardrails. FEMORPH applies the same lesson: durable engineering software depends on culture, interfaces, and maintainability as much as algorithms.
              </Typography>
            </Grid>
            <Grid item xs={12} md={8}>
              <Grid container spacing={2}>
                {openSourceProjects.map((project) => (
                  <Grid item xs={12} sm={6} lg={4} key={project.repo}>
                    <Box
                      component={Link}
                      href={project.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="none"
                      sx={{
                        display: 'block',
                        height: '100%',
                        p: 2.5,
                        bgcolor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'divider',
                        color: 'text.primary',
                        '&:hover': {
                          borderColor: 'primary.main',
                        },
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                        <StarBorderOutlined sx={{ color: 'secondary.main', fontSize: 20 }} />
                        <Typography variant="h6">{project.stars}</Typography>
                        <Typography variant="body2" color="text.secondary">stars</Typography>
                      </Stack>
                      <Typography variant="h6" sx={{ mb: 0.5 }}>
                        {project.name}
                      </Typography>
                      <Typography variant="caption" color="primary.main" sx={{ display: 'block', mb: 1 }}>
                        {project.repo}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>
                        {project.detail}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box component="section" sx={{ bgcolor: 'background.paper', borderY: '1px solid', borderColor: 'divider' }}>
        <Container maxWidth="xl" sx={{ py: { xs: 7, md: 10 } }}>
          <Grid container spacing={{ xs: 5, md: 8 }}>
            <Grid item xs={12} md={6}>
              <Typography variant="overline" color="secondary.dark">
                Engineering lineage
              </Typography>
              <Typography variant="h3" sx={{ mt: 1, mb: 2 }}>
                FEMORPH comes from a company with deep software, analysis, and research credentials.
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8, mb: 3 }}>
                AEA has been part of United States Air Force turbomachinery analysis, AFRL software and research support, Ansys product engineering, open-source scientific Python, and production aerospace analysis. That background is directly reflected in how FEMORPH is built, tested, packaged, and supported.
              </Typography>
              <Stack spacing={1.5}>
                {companyLineage.map((highlight) => (
                  <Stack direction="row" spacing={1.25} alignItems="flex-start" key={highlight}>
                    <VerifiedOutlined sx={{ color: 'primary.main', mt: 0.2, fontSize: 20, flexShrink: 0 }} />
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                      {highlight}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ p: { xs: 2.5, md: 3 }, bgcolor: '#eef3f1', border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="h5" sx={{ mb: 2 }}>
                  Relevant accomplishments
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="overline" color="text.secondary">
                      Open source
                    </Typography>
                    <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                      PyVista, PyMAPDL, pymeshfix, tetgen, pyacvd, and mesh-analysis tooling used across scientific Python and engineering automation.
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="overline" color="text.secondary">
                      Ansys leadership
                    </Typography>
                    <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                      Helped turn Python interfaces for major Ansys products into the broader PyAnsys ecosystem, with shared documentation, automation, testing, and release practices.
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="overline" color="text.secondary">
                      Research
                    </Typography>
                    <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                      Published work in ASME, AIAA, JOSS, and related venues across model updating, mistuning, dynamic testing, and mesh analysis.
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="overline" color="text.secondary">
                      Patents
                    </Typography>
                    <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                      Named inventor on patents for turbine airfoil repair and strain-gauge to blade-tip-timing correlation.
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box component="section" sx={{ bgcolor: 'background.default' }}>
        <Container maxWidth="xl" sx={{ py: { xs: 7, md: 10 } }}>
          <Grid container spacing={{ xs: 5, md: 8 }}>
            <Grid item xs={12} md={5}>
              <Typography variant="overline" color="secondary.dark">
                Product family
              </Typography>
              <Typography variant="h3" sx={{ mt: 1, mb: 2 }}>
                FEMORPH is an ecosystem, not a single script.
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                The commercial product is supported by a software stack that includes native code, Python APIs, desktop tooling, licensing, private package distribution, documentation, and CI. That stack exists because real engineering workflows need repeatability, traceability, and support across operating systems and solver formats.
              </Typography>
            </Grid>
            <Grid item xs={12} md={7}>
              <Grid container spacing={2}>
                {productStack.map((item) => (
                  <Grid item xs={12} sm={6} key={item.name}>
                    <Box sx={{ height: '100%', p: 2.5, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="h6" sx={{ mb: 1 }}>
                        {item.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                        {item.detail}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box component="section" sx={{ bgcolor: '#f5f0e9', borderY: '1px solid', borderColor: 'divider' }}>
        <Container maxWidth="xl" sx={{ py: { xs: 7, md: 10 } }}>
          <Grid container spacing={{ xs: 5, md: 8 }} alignItems="flex-start">
            <Grid item xs={12} md={5}>
              <Typography variant="overline" color="secondary.dark">
                Research basis
              </Typography>
              <Typography variant="h3" sx={{ mt: 1, mb: 2 }}>
                Built on a long research thread in as-manufactured analysis.
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                FEMORPH grew out of work on reverse engineering, optical measurement, finite element model updating, geometric mistuning, and turbomachinery dynamics. Those topics remain central to the product: the software is designed around measured hardware, not idealized geometry alone.
              </Typography>
            </Grid>
            <Grid item xs={12} md={7}>
              <Grid container spacing={2}>
                {researchThemes.map((themeText) => (
                  <Grid item xs={12} sm={6} key={themeText}>
                    <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ height: '100%', p: 2.25, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
                      <ArticleOutlined sx={{ color: 'primary.main', mt: 0.2, fontSize: 22, flexShrink: 0 }} />
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                        {themeText}
                      </Typography>
                    </Stack>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box component="section" sx={{ bgcolor: 'background.paper' }}>
        <Container maxWidth="xl" sx={{ py: { xs: 7, md: 10 } }}>
          <Grid container spacing={{ xs: 5, md: 8 }} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="overline" color="secondary.dark">
                Why this matters
              </Typography>
              <Typography variant="h3" sx={{ mt: 1, mb: 2 }}>
                Professional software requires professional provenance.
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8, mb: 3 }}>
                FEMORPH is written by a company that understands both the numerical methods and the operational environment where the software is used. AEA maintains the algorithms, packaging, testing, support, documentation, release process, and engineering judgment required to make scan-driven analysis practical for serious programs.
              </Typography>
              <Button component={RouterLink} to="/support" variant="contained" startIcon={<BusinessOutlined />}>
                Contact AEA
              </Button>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ p: { xs: 2.5, md: 3 }, bgcolor: '#172126', color: 'white' }}>
                <Typography variant="h5" sx={{ mb: 2 }}>
                  Public references
                </Typography>
                <Stack spacing={1.25}>
                  {publicLinks.map((link) => (
                    <Link key={link.href} href={link.href} target="_blank" rel="noopener noreferrer" underline="hover" sx={{ color: 'rgba(255,255,255,0.88)' }}>
                      {link.label}
                    </Link>
                  ))}
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default About;
